import { useState, useCallback, useEffect, useRef, useContext } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import miniSvgDataUri from 'mini-svg-data-uri'

import { features } from '@h2/components/media'
import {
  PB90T,
  PB97T,
  SLB,
  SLB90T,
  SO,
  SO90T,
  SG,
  SG90T,
  SY,
  SY90T,
  SPK,
  SPK90T
} from '@h2/components/colors'
import { lists } from '@h2/components/mixins'
import { RetractIcon } from '@h2/components/icons/retract'
import { Skeleton } from '@h2/components/loading/skeleton'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import activitiesSvg from '@h2/assets/svg/illustrations/source/activities.svg'
import researchSvg from '@h2/assets/svg/illustrations/source/research.svg'
import interestProfilerSvg from '@h2/assets/svg/illustrations/interestProfiler.svg'

import { Panel as PanelBase } from './panel'

import { Context } from '../../context'

const borderWidth = 1
const svgMap = {
  Activities: miniSvgDataUri(activitiesSvg),
  Research: miniSvgDataUri(researchSvg),
  'My Stuff': interestProfilerSvg
}
const before = css`
  content: '';
  display: block;
  height: 100%;
  width: calc(100% + 4px);
  position: absolute;
  top: 0;
  left: calc(-100% - 6px);
  transition: left 0.25s ease-in-out;
  border-radius: 4px;
  border-left: 2px solid transparent;
  border-right: 2px solid transparent;
  background-color: transparent;
`
const link = css`
  display: block;
  padding: 10px 0;
  cursor: pointer;

  figure {
    img {
      display: none;
    }

    figcaption {
      h3 {
        font-size: 20px;
        margin: 0;
        letter-spacing: normal;
      }

      p {
        display: none;
      }
    }
  }

  &:hover,
  &:focus {
    text-decoration: none;
  }

  @media (${features.minWidth.laptop}) {
    background-color: white;
    border: 2px solid transparent;
    border-top: none;
    border-bottom: none;
    border-radius: 4px;
    padding: 12px;
    position: relative;

    figure {
      display: flex;
      align-items: flex-start;

      img {
        display: inline;
        height: 75px;
      }

      figcaption {
        margin-left: 10px;

        h3 {
          font-size: 24px;
          margin: 0 0 3px 0;
          letter-spacing: 0.8px;
        }
      }
    }

    &::before {
      ${before};
    }

    &:hover {
      &::before {
        left: -2px;
      }
    }

    &.activities {
      border-left-color: ${SLB};

      &::before {
        border-right-color: ${SLB};
      }

      &:hover {
        background-color: ${SLB90T};
      }
    }

    &.research {
      border-left-color: ${SO};

      &::before {
        border-right-color: ${SO};
      }

      &:hover {
        background-color: ${SO90T};
      }
    }

    &.my-stuff {
      border-left-color: ${SG};

      &::before {
        border-right-color: ${SG};
      }

      &:hover {
        background-color: ${SG90T};
      }
    }

    &.maintenance {
      border-left-color: ${SPK};

      &::before {
        border-right-color: ${SPK};
      }

      &:hover {
        background-color: ${SPK90T};
      }
    }

    &.management {
      border-left-color: ${SY};

      &::before {
        border-right-color: ${SY};
      }

      &:hover {
        background-color: ${SY90T};
      }
    }
  }
`
const Panel = styled(PanelBase)`
  @media (${features.minWidth.laptop}) {
    > div:first-child {
      padding: 0;
    }
  }
`
const Wrap = styled.div`
  height: ${({ height }) => height};

  @media (${features.minWidth.laptop}) {
    height: auto;
  }
`
const List = styled.ul`
  ${lists};
  position: relative;
  z-index: 1;
  overflow: hidden;
  background-color: ${PB97T};
  height: ${({ isOpen, height, length }) =>
    isOpen ? `calc(${length} * ${height} + ${length - 1} * ${borderWidth}px)` : height};
  transition: height 0.25s ease-in-out;

  > li {
    border-top: ${borderWidth}px solid white;

    &:first-child {
      border-top: none;
    }
  }

  @media (${features.minWidth.laptop}) {
    height: auto;

    > li {
      padding-right: 30px;
      border-top: none;

      &.active {
        a {
          border-left: 2px solid transparent;
          border-right: 2px solid transparent;

          &::before {
            display: none;
          }

          &.activities {
            background-color: ${SLB90T};
            border-left-color: ${SLB};
            border-right-color: ${SLB};
          }

          &.research {
            background-color: ${SO90T};
            border-left-color: ${SO};
            border-right-color: ${SO};
          }

          &.my-stuff {
            background-color: ${SG90T};
            border-left-color: ${SG};
            border-right-color: ${SG};
          }

          &.maintenance {
            background-color: ${SPK90T};
            border-left-color: ${SPK};
            border-right-color: ${SPK};
          }

          &.management {
            background-color: ${SY90T};
            border-left-color: ${SY};
            border-right-color: ${SY};
          }
        }
      }

      &:last-child {
        margin-bottom: 15px;
      }

      &:nth-child(n + 2) {
        margin-top: 15px;
      }
    }
  }
`
const Retract = styled(RetractIcon)`
  cursor: pointer;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(0deg)' : 'rotate(180deg)')};
  transition: transform 0.25s ease-in-out;

  @media (${features.minWidth.laptop}) {
    display: none;
  }
`
const Link = styled.a`
  ${link};
`
const Slot = styled.li`
  ${link};

  > div:first-child > div:first-child {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`
const toClassName = str => str.replace(' ', '-').toLowerCase()
const Categories = ({ categories, itemsRef }) => {
  const slotRef = useRef()
  const { isMenuFetching, isMobile } = useContext(Context)
  const [active, setActive] = useState(categories[0])
  const [isOpen, setIsOpen] = useState(false)
  const [height, setHeight] = useState('48px')
  const handleOnClick = useCallback(
    evt => {
      const section = document.querySelector(evt.currentTarget.getAttribute('href'))

      evt.stopPropagation()
      evt.preventDefault()
      setIsOpen(false)

      if (section && itemsRef.current) {
        const itemsNode = itemsRef.current
        const sectionRect = section.getBoundingClientRect()
        const itemsRect = itemsNode.getBoundingClientRect()
        let top = itemsNode.scrollTop + sectionRect.top - itemsRect.top

        if (isMobile) {
          const headingRect = section.querySelector('h3').getBoundingClientRect()
          const centering = Math.abs(parseInt(height) - headingRect.height) / 2
          const padding = 2 * parseInt(window.getComputedStyle(section).paddingTop)

          top = top + centering + padding
        }

        const firstFocusableLink = section.querySelector('a')

        if (firstFocusableLink) {
          firstFocusableLink.focus()
        }

        itemsNode.scrollTo({ top, behavior: 'smooth' })
      }
    },
    [itemsRef, height, isMobile]
  )

  useEffect(() => {
    if (slotRef.current) {
      setHeight(`${slotRef.current.getBoundingClientRect().height}px`)
    }
  }, [])

  useEffect(() => {
    setActive(categories[0])
  }, [categories])

  useEffect(() => {
    let observer = null

    if (categories.length && itemsRef.current) {
      const itemsRect = itemsRef.current.getBoundingClientRect()
      const sections = Array.from(itemsRef.current.querySelectorAll('section[id]'))

      observer = new IntersectionObserver(
        entries => {
          entries.forEach(({ isIntersecting, boundingClientRect, target }) => {
            const { top: targetTop } = boundingClientRect

            if (targetTop < itemsRect.top) {
              const sectionTitle = target.querySelector('h3').textContent
              let index = categories.findIndex(
                category => category.title === sectionTitle
              )

              sections.forEach(section => section.classList.remove('active'))

              if (!isIntersecting) {
                /**
                 * Section scrolled off the page from the top,
                 * so the next section, if available, is active.
                 */
                index = Math.min(index + 1, sections.length - 1)
              }

              setActive(categories[index])
              sections[index]?.classList.add('active')
            }
          })
        },
        { threshold: 0 }
      )

      sections.forEach(section => observer.observe(section))
    }

    return () => observer?.disconnect()
  }, [categories, itemsRef])

  return (
    <Wrap height={height}>
      <List
        isOpen={isOpen}
        height={height}
        length={categories.length}
        onClick={() => {
          setIsOpen(!isOpen)
        }}>
        {active && isMobile && !isMenuFetching && (
          <Slot ref={slotRef} className={toClassName(active?.title ?? '')}>
            <Panel color={PB97T}>
              <figure>
                <img src={svgMap[active.title]} alt={`category ${active.title}`} />
                <figcaption>
                  <Heading level="3">{active.title}</Heading>
                  <Text size="small">{active.description}</Text>
                </figcaption>
              </figure>
              <Retract isOpen={isOpen} color={PB90T} />
            </Panel>
          </Slot>
        )}
        {isMenuFetching && !isMobile
          ? Array(3)
              .fill()
              .map((item, idx) => (
                <li key={idx}>
                  <Skeleton display="block" height="110px" />
                </li>
              ))
          : categories
              .filter(category => !isMobile || category.title !== active?.title)
              .map(({ title, description }) => (
                <li key={title} className={title === active?.title ? 'active' : null}>
                  <Panel color={PB97T}>
                    <Link
                      href={`#${toClassName(title)}`}
                      className={toClassName(title)}
                      onClick={handleOnClick}>
                      <figure>
                        <img
                          src={svgMap[title] ?? interestProfilerSvg}
                          alt={`category ${title}`}
                        />
                        <figcaption>
                          <Heading level="3">{title}</Heading>
                          <Text size="small">{description}</Text>
                        </figcaption>
                      </figure>
                    </Link>
                  </Panel>
                </li>
              ))}
      </List>
    </Wrap>
  )
}

Categories.propTypes = {
  itemsRef: PropTypes.object.isRequired,
  categories: PropTypes.array.isRequired
}

export { Categories }
